import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { LocalNotification } from '@amzn/aac-chrome-internal-api';
import { RootState } from 'src/app/store';
import { logWithTakt } from 'src/commons/utils/logWithTakt';
import { BANNER_LEVEL, selectViewType, VIEW_TYPE } from 'src/app/slice';
import { getPartitionedBanners } from '../bannerUtils';
import { bannerLocalStorageKey, bannerMetricsPublisher } from '../types';
import { ConsolidatedBanner } from '../ConsolidatedBanner/ConsolidatedBanner';
import { SingleBanner } from '../SingleBanner/SingleBanner';
import { SingleBannerWithModal } from '../SingleBanner/SingleBannerWithModal';

const TRUNCATE_LIMIT = 141;

interface BannerProps {
  banners: LocalNotification[];
  bannerLevel: BANNER_LEVEL;
  viewType: VIEW_TYPE;
  acesDismissFn?: (localNotification: LocalNotification) => void;
}

export const Banner: React.VFC<BannerProps> = ({
  banners,
  bannerLevel,
  acesDismissFn,
  viewType,
}) => {
  const [localBanners, setLocalBanners] = useState([...banners]);
  const [partitionedBanners, setPartitionedBanners] = useState(
    getPartitionedBanners(banners),
  );

  useEffect(() => {
    setPartitionedBanners(getPartitionedBanners(banners));
    setLocalBanners(banners);
  }, [banners]);

  const updateBanners = (bannersForUpdate) => {
    setPartitionedBanners(getPartitionedBanners(bannersForUpdate));
    setLocalBanners(bannersForUpdate);
  };

  if (partitionedBanners.total === 0) {
    return null;
  }

  const dismissBanner = (banner, localStorageItems) => {
    if (banner.notificationType === 'aces' && acesDismissFn) {
      // call aces to properly dismiss the banner object
      acesDismissFn(banner);
      localStorageItems.push(banner.id);
    } else if (!localStorageItems.includes(banner.id)) {
      localStorageItems.push(banner.id);
    }
  };

  const dismissBanners = (banners) => {
    const localStorageItems = JSON.parse(
      localStorage.getItem(bannerLocalStorageKey[bannerLevel]) || '[]',
    );

    banners.map((banner) => {
      dismissBanner(banner, localStorageItems);
    });

    localStorage.setItem(
      bannerLocalStorageKey[bannerLevel],
      JSON.stringify(localStorageItems),
    );

    updateBanners([]);
  };

  const onConsolidatedBannersDismiss = () => {
    // user can only dismiss consolidated banner when there's no error banner
    // so dismissing all banners here regardless of banner type
    dismissBanners(localBanners);

    bannerMetricsPublisher[bannerLevel].publishCounterMonitor(
      'consolidatedBannerClosed',
      1,
    );
    logWithTakt(`${bannerLevel}BannerClosed`, {
      bannerId: 'consolidatedBanner',
    });
  };

  // Render consolidated banner if there are more than 1 banner
  if (partitionedBanners.total > 1) {
    return (
      <ConsolidatedBanner
        partitionedBanners={partitionedBanners}
        onBannerDismiss={onConsolidatedBannersDismiss}
        bannerLevel={bannerLevel}
      />
    );
  }

  const onSingleBannerDismiss = (dismissBanner: LocalNotification) => {
    dismissBanners([dismissBanner]);

    bannerMetricsPublisher[bannerLevel].publishCounterMonitor(
      `singleBannerClosed:${dismissBanner.id}`,
      1,
    );
    logWithTakt(`${bannerLevel}BannerClosed`, { bannerId: dismissBanner.id });
  };

  const banner = localBanners[0];
  let bannerMessageLength = 0;
  if (banner.acesBannerContent)
    banner.acesBannerContent.map(
      (content) => (bannerMessageLength += content.text.length),
    );
  else if (banner.title && banner.title.length > 0)
    bannerMessageLength = banner.title.length;
  else if (banner.message && banner.message.length > 0)
    bannerMessageLength = banner.message.length;

  const shouldTruncate =
    bannerMessageLength > TRUNCATE_LIMIT &&
    viewType !== VIEW_TYPE.MOBILE &&
    bannerLevel === BANNER_LEVEL.CONSOLE;
  const hasModal = shouldTruncate || (banner.title && banner.message);
  // Render banner with a `learn more` modal in two cases:
  // 1. banner text too long, this will show '{truncated text}... Learn more' which opens full text modal
  // 2. both 'title' and 'message' fields exist, this will show '{title} Learn more' which opens {message} modal
  if (hasModal) {
    return (
      <SingleBannerWithModal
        banner={banner}
        onDismissClick={onSingleBannerDismiss}
        bannerLevel={bannerLevel}
        isRenderedInModal={false}
      />
    );
  }

  // Render single banner if there's only 1 banner
  return (
    <SingleBanner
      banner={banner}
      onDismissClick={onSingleBannerDismiss}
      bannerLevel={bannerLevel}
      isRenderedInModal={false}
    />
  );
};

const BannerConnector = connect((state: RootState, ownProps) => {
  return {
    ...ownProps,
    viewType: selectViewType(state),
  };
});

export const ConnectedBanner = BannerConnector(Banner);
